import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateCustomerRequest, CreateVehicleRequest, PatchCustomerRequest, PatchVehicleRequest } from '@wf/shared'
import { api } from '../api/client'

export function useCustomersList() {
  return useQuery({ queryKey: ['customers'], queryFn: api.listCustomers })
}

/** Kundendetail (Stammdaten + Fahrzeuge + Auftragshistorie). Lädt nur bei gewählter id. */
export function useCustomer(id: number | null) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => api.getCustomer(id as number),
    enabled: id != null,
  })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCustomerRequest) => api.createCustomer(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers'], exact: true }),
  })
}

/**
 * Stammdaten ändern mit Optimistic Concurrency: die zuletzt gelesene `version` ist Pflicht. Bei 409
 * (Versionskonflikt) wirft der Client einen ApiError — die UI lädt dann den frischen Stand neu, statt
 * blind zu überschreiben.
 */
export function usePatchCustomer(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PatchCustomerRequest) => api.patchCustomer(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', id] })
      void qc.invalidateQueries({ queryKey: ['customers'], exact: true })
    },
  })
}

/** DSGVO-Datenauskunft: lädt den Export (Kunde + Fahrzeuge + Aufträge) als Mutation, damit der Aufrufer
 *  den Lade-/Fehlerzustand am Knopf hat und das Ergebnis selbst als Datei herunterlädt. */
export function useExportCustomer(id: number) {
  return useMutation({ mutationFn: () => api.exportCustomer(id) })
}

/** Kunde anonymisieren (DSGVO-Löschpfad ohne Datenverlust): Stammdaten werden serverseitig neutralisiert,
 *  Aufträge bleiben (GoBD). Erfolg lädt Detail + Liste neu, damit der anonymisierte Stand sofort sichtbar ist. */
export function useAnonymizeCustomer(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.anonymizeCustomer(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', id] })
      void qc.invalidateQueries({ queryKey: ['customers'], exact: true })
    },
  })
}

/** Kunde hart löschen. 409 {blocked:'has_orders'} (ApiError) bleibt für den Aufrufer sichtbar, damit die UI
 *  stattdessen die Anonymisierung anbieten kann. Erfolg frischt die Liste; das Detail räumt der Aufrufer. */
export function useDeleteCustomer(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.deleteCustomer(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers'], exact: true }),
  })
}

/**
 * Fahrzeug-Stammdaten ändern (Kennzeichen/Modell/FIN) mit Optimistic Concurrency: `version` ist Pflicht.
 * Das Fahrzeug hängt am Kundendetail (CustomerDetailResponse.vehicles), darum lädt der Erfolg dieses Detail
 * neu; bei 409 (oder jedem Fehler) ebenso frisch nachladen statt blind zu überschreiben.
 */
export function usePatchVehicle(customerId: number) {
  const qc = useQueryClient()
  const reload = () => void qc.invalidateQueries({ queryKey: ['customers', customerId] })
  return useMutation({
    mutationFn: (vars: { vehicleId: number; body: PatchVehicleRequest }) => api.patchVehicle(vars.vehicleId, vars.body),
    onSuccess: reload,
    onError: reload,
  })
}

/** Die Fahrzeug-Liste hängt am Kundendetail — alle Fahrzeug-Mutationen frischen es nach. */
function useVehicleMutation<V>(customerId: number, run: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers', customerId] }),
  })
}

/** Fahrzeug zu diesem Kunden anlegen (idempotent über client_uuid). */
export function useCreateVehicle(customerId: number) {
  return useVehicleMutation(customerId, (body: CreateVehicleRequest) => api.createVehicle(body))
}
/** Fahrzeug hart löschen. */
export function useDeleteVehicle(customerId: number) {
  return useVehicleMutation(customerId, (vehicleId: number) => api.deleteVehicle(vehicleId))
}
/** Fahrzeug anonymisieren (DSGVO-Löschpfad ohne Datenverlust — Aufträge bleiben). */
export function useAnonymizeVehicle(customerId: number) {
  return useVehicleMutation(customerId, (vehicleId: number) => api.anonymizeVehicle(vehicleId))
}

/** Auftragshistorie eines Fahrzeugs (nur laden, wenn aufgeklappt → enabled). */
export function useVehicleHistory(vehicleId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['vehicleHistory', vehicleId],
    queryFn: () => api.vehicleHistory(vehicleId),
    enabled,
  })
}
